import { Edit, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import type { Plan } from '@/components/Configuracao/plansTabTypes'
import { PLAN_TYPE_LABELS } from '@/components/Configuracao/plansTabTypes'
import {
  countPlanScopeItems,
  formatBRL,
  groupPlansByCategory,
  PLAN_CATEGORY_COLORS,
  resolvePlanTypeForCategory,
  supportPricingLabel,
} from '@/components/Configuracao/plansTabUtils'

type Props = {
  plans: Plan[]
  loading: boolean
  onEdit: (plan: Plan) => void
  onDelete: (id: string) => void
}

export function PlansTabList({ plans, loading, onEdit, onDelete }: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (plans.length === 0) {
    return (
      <div className="text-center text-[var(--text-muted)] py-16 text-[13px] rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)]">
        Nenhum plano cadastrado. Central de Inteligência vazia.
      </div>
    )
  }

  return (
    <>
      {Object.entries(groupPlansByCategory(plans)).map(([categoryKey, categoryPlans]) => {
        if (categoryPlans.length === 0) return null
        const colors = PLAN_CATEGORY_COLORS[categoryKey] || PLAN_CATEGORY_COLORS.OUTROS

        return (
          <div key={categoryKey} className="space-y-3">
            <div
              className="flex items-center gap-3 px-4 py-2 rounded-lg border-l-4"
              style={{ background: colors.bg, borderColor: colors.border }}
            >
              <h4
                className="font-bold uppercase tracking-wider m-0"
                style={{ fontSize: '11px', color: colors.text }}
              >
                {categoryKey}
              </h4>
              <Badge variant="muted" className="text-[10px] px-2 py-0.5">
                {categoryPlans.length} {categoryPlans.length === 1 ? 'plano' : 'planos'}
              </Badge>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[var(--glass-border)]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--glass-border)] bg-[var(--glass-bg)]">
                    {['Nome', 'Categoria', 'Tipo', 'Entregas', 'Esforço', 'Modalidade', 'Ações'].map(
                      (h) => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 font-medium text-[var(--font-color-label)] uppercase tracking-wider"
                          style={{ fontSize: 'var(--font-size-label)' }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {categoryPlans.map((p) => {
                    const cfg = p.config || {}
                    const deliverablesCount = countPlanScopeItems(p)
                    const t = PLAN_TYPE_LABELS[resolvePlanTypeForCategory(p.category, p.type) ?? 'RECORRENTE']

                    return (
                      <tr
                        key={p.id}
                        className="border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-[var(--text-primary)] text-[13px]">{p.name}</span>
                            {p.description && (
                              <span className="text-[var(--text-muted)] text-[11px] line-clamp-1 mt-0.5">
                                {p.description.substring(0, 60)}
                                {p.description.length > 60 ? '...' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[var(--text-muted)] text-[13px]">{p.category || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={t.variant} className="text-[10px]">
                            {t.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="info">{deliverablesCount} itens</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="warning">{Number(cfg.totalHours || 0).toFixed(1)}h</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-[#27a644] text-[13px]">
                            {formatBRL(Number(p.baseValue || 0))}
                          </span>
                          <span className="mt-0.5 block text-[10px] text-[var(--text-muted)]">
                            {supportPricingLabel(cfg)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => onEdit(p)}
                              title="Editar Matriz"
                              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--glass-bg)] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(p.id)}
                              title="Excluir Plano"
                              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[#e5484d] hover:bg-[rgba(229,72,77,0.1)] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
    </>
  )
}
